/**
 * Labels the consent UI owns: actions, status text, accessible names and the default helper
 * line. Operator-authored policy copy (titles, banner text, category labels and descriptions)
 * is never translated here; it is rendered exactly as the policy delivers it.
 *
 * The languages match `CONSENT_PROVIDER_LOCALES` in `src/shared`, the set the server localizes
 * provider purposes into. `consent-i18n.test.ts` keeps the two lists equal.
 */
export const CONSENT_UI_LOCALES = ['en', 'de', 'tr', 'ru', 'ar', 'pl', 'ro', 'it', 'uk'] as const;
export type ConsentUiLocale = (typeof CONSENT_UI_LOCALES)[number];

const RTL_LOCALES: readonly ConsentUiLocale[] = ['ar'];

export interface ConsentUiText {
  acceptAll: string;
  rejectAll: string;
  manageChoices: string;
  saveChoices: string;
  close: string;
  alwaysOn: string;
  preferencesIntro: string;
  privacyPolicy: string;
  /** Accessible name of the site's own privacy link. */
  privacyPolicyNewTab: string;
  /** Accessible name of a provider's privacy link. */
  providerPrivacyPolicy: (providerName: string) => string;
  servicesInCategory: string;
  controllerDetails: string;
  /** Small label above the dialog title. */
  privacyEyebrow: string;
  /** Accessible name of the group of optional category checkboxes. */
  optionalCategories: string;
  /** Disclosure that explains every category in place. */
  aboutCategories: string;
  providers: string;
  searchPlaceholder: string;
  /** Accessible name of the provider search field. */
  searchLabel: string;
  details: string;
  previousPage: string;
  nextPage: string;
  /** Accessible name of the provider pagination. */
  providerPages: string;
  /** "1–5 of 12 providers" */
  providerRange: (from: number, to: number, total: number) => string;
  /** "1 provider", "12 providers" */
  providerCount: (count: number) => string;
  noMatches: string;
  clearSearch: string;
  noProviders: string;
}

/** Plural category of a count in a locale, for the languages whose nouns inflect by number. */
function plural(locale: string, count: number): Intl.LDMLPluralRule {
  try {
    return new Intl.PluralRules(locale).select(count);
  } catch {
    return count === 1 ? 'one' : 'other';
  }
}

const TEXT: Record<ConsentUiLocale, ConsentUiText> = {
  en: {
    acceptAll: 'Accept all',
    rejectAll: 'Reject all',
    manageChoices: 'Manage choices',
    saveChoices: 'Save choices',
    close: 'Close',
    alwaysOn: 'Always on',
    preferencesIntro: 'Choose which optional services may run on this site. Necessary services are always on.',
    privacyPolicy: 'Privacy policy',
    privacyPolicyNewTab: 'Privacy policy (opens in a new tab)',
    providerPrivacyPolicy: (name) => `Privacy policy of ${name} (opens in a new tab)`,
    servicesInCategory: 'Services in this category',
    controllerDetails: 'Controller details',
    privacyEyebrow: 'Privacy',
    optionalCategories: 'Optional categories',
    aboutCategories: 'About these categories',
    providers: 'Providers',
    searchPlaceholder: 'Search',
    searchLabel: 'Search providers or purposes',
    details: 'Details',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    providerPages: 'Provider pages',
    providerRange: (from, to, total) => `${from}–${to} of ${total} providers`,
    providerCount: (count) => (count === 1 ? '1 provider' : `${count} providers`),
    noMatches: 'No matching providers.',
    clearSearch: 'Clear search',
    noProviders: 'No external providers are listed for this site.',
  },
  de: {
    acceptAll: 'Alle akzeptieren',
    rejectAll: 'Alles ablehnen',
    manageChoices: 'Auswahl verwalten',
    saveChoices: 'Auswahl speichern',
    close: 'Schließen',
    alwaysOn: 'Immer aktiv',
    preferencesIntro:
      'Sie entscheiden, welche optionalen Dienste auf dieser Website aktiv sein dürfen. Erforderliche Dienste sind immer aktiv.',
    privacyPolicy: 'Datenschutzerklärung',
    privacyPolicyNewTab: 'Datenschutzerklärung (öffnet in neuem Tab)',
    providerPrivacyPolicy: (name) => `Datenschutzerklärung von ${name} (öffnet in neuem Tab)`,
    servicesInCategory: 'Dienste in dieser Kategorie',
    controllerDetails: 'Verantwortlicher',
    privacyEyebrow: 'Datenschutz',
    optionalCategories: 'Optionale Kategorien',
    aboutCategories: 'Über diese Kategorien',
    providers: 'Anbieter',
    searchPlaceholder: 'Suchen',
    searchLabel: 'Anbieter oder Zweck suchen',
    details: 'Details',
    previousPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    providerPages: 'Anbieterseiten',
    providerRange: (from, to, total) => `${from}–${to} von ${total} Anbietern`,
    providerCount: (count) => `${count} Anbieter`,
    noMatches: 'Keine passenden Anbieter gefunden.',
    clearSearch: 'Suche zurücksetzen',
    noProviders: 'Für diese Website sind keine externen Anbieter aufgeführt.',
  },
  tr: {
    acceptAll: 'Tümünü kabul et',
    rejectAll: 'Tümünü reddet',
    manageChoices: 'Seçimleri yönet',
    saveChoices: 'Seçimleri kaydet',
    close: 'Kapat',
    alwaysOn: 'Her zaman açık',
    preferencesIntro:
      'Bu sitede hangi isteğe bağlı hizmetlerin çalışabileceğini siz seçersiniz. Gerekli hizmetler her zaman açıktır.',
    privacyPolicy: 'Gizlilik politikası',
    privacyPolicyNewTab: 'Gizlilik politikası (yeni sekmede açılır)',
    providerPrivacyPolicy: (name) => `${name} gizlilik politikası (yeni sekmede açılır)`,
    servicesInCategory: 'Bu kategorideki hizmetler',
    controllerDetails: 'Veri sorumlusu',
    privacyEyebrow: 'Gizlilik',
    optionalCategories: 'İsteğe bağlı kategoriler',
    aboutCategories: 'Bu kategoriler hakkında',
    providers: 'Sağlayıcılar',
    searchPlaceholder: 'Ara',
    searchLabel: 'Sağlayıcı veya amaç ara',
    details: 'Ayrıntılar',
    previousPage: 'Önceki sayfa',
    nextPage: 'Sonraki sayfa',
    providerPages: 'Sağlayıcı sayfaları',
    providerRange: (from, to, total) => `${total} sağlayıcıdan ${from}–${to}`,
    providerCount: (count) => `${count} sağlayıcı`,
    noMatches: 'Eşleşen sağlayıcı yok.',
    clearSearch: 'Aramayı temizle',
    noProviders: 'Bu site için listelenmiş harici sağlayıcı yok.',
  },
  ru: {
    acceptAll: 'Принять все',
    rejectAll: 'Отклонить все',
    manageChoices: 'Настроить выбор',
    saveChoices: 'Сохранить выбор',
    close: 'Закрыть',
    alwaysOn: 'Всегда включено',
    preferencesIntro:
      'Выберите, какие необязательные сервисы могут работать на этом сайте. Необходимые сервисы всегда включены.',
    privacyPolicy: 'Политика конфиденциальности',
    privacyPolicyNewTab: 'Политика конфиденциальности (откроется в новой вкладке)',
    providerPrivacyPolicy: (name) => `Политика конфиденциальности: ${name} (откроется в новой вкладке)`,
    servicesInCategory: 'Сервисы в этой категории',
    controllerDetails: 'Оператор данных',
    privacyEyebrow: 'Конфиденциальность',
    optionalCategories: 'Необязательные категории',
    aboutCategories: 'Об этих категориях',
    providers: 'Поставщики',
    searchPlaceholder: 'Поиск',
    searchLabel: 'Поиск поставщиков или целей',
    details: 'Подробнее',
    previousPage: 'Предыдущая страница',
    nextPage: 'Следующая страница',
    providerPages: 'Страницы поставщиков',
    providerRange: (from, to, total) => `${from}–${to} из ${total}`,
    providerCount: (count) =>
      `${count} ${{ one: 'поставщик', few: 'поставщика', many: 'поставщиков' }[plural('ru', count) as 'one' | 'few' | 'many'] ?? 'поставщика'}`,
    noMatches: 'Подходящих поставщиков нет.',
    clearSearch: 'Сбросить поиск',
    noProviders: 'Для этого сайта не указаны внешние поставщики.',
  },
  ar: {
    acceptAll: 'قبول الكل',
    rejectAll: 'رفض الكل',
    manageChoices: 'إدارة الخيارات',
    saveChoices: 'حفظ الخيارات',
    close: 'إغلاق',
    alwaysOn: 'مفعّل دائمًا',
    preferencesIntro: 'اختر الخدمات الاختيارية التي يُسمح بتشغيلها على هذا الموقع. تظل الخدمات الضرورية مفعّلة دائمًا.',
    privacyPolicy: 'سياسة الخصوصية',
    privacyPolicyNewTab: 'سياسة الخصوصية (تُفتح في علامة تبويب جديدة)',
    providerPrivacyPolicy: (name) => `سياسة الخصوصية لـ ${name} (تُفتح في علامة تبويب جديدة)`,
    servicesInCategory: 'الخدمات في هذه الفئة',
    controllerDetails: 'المسؤول عن المعالجة',
    privacyEyebrow: 'الخصوصية',
    optionalCategories: 'الفئات الاختيارية',
    aboutCategories: 'حول هذه الفئات',
    providers: 'مقدّمو الخدمات',
    searchPlaceholder: 'بحث',
    searchLabel: 'ابحث عن مقدّم خدمة أو غرض',
    details: 'التفاصيل',
    previousPage: 'الصفحة السابقة',
    nextPage: 'الصفحة التالية',
    providerPages: 'صفحات مقدّمي الخدمات',
    providerRange: (from, to, total) => `${from}–${to} من ${total}`,
    providerCount: (count) => `مقدّمو الخدمات: ${count}`,
    noMatches: 'لا يوجد مقدّمو خدمات مطابقون.',
    clearSearch: 'مسح البحث',
    noProviders: 'لا يوجد مقدّمو خدمات خارجيون مدرجون لهذا الموقع.',
  },
  pl: {
    acceptAll: 'Akceptuj wszystkie',
    rejectAll: 'Odrzuć wszystkie',
    manageChoices: 'Zarządzaj wyborem',
    saveChoices: 'Zapisz wybór',
    close: 'Zamknij',
    alwaysOn: 'Zawsze włączone',
    preferencesIntro: 'Zdecyduj, które opcjonalne usługi mogą działać w tej witrynie. Niezbędne usługi są zawsze włączone.',
    privacyPolicy: 'Polityka prywatności',
    privacyPolicyNewTab: 'Polityka prywatności (otwiera się w nowej karcie)',
    providerPrivacyPolicy: (name) => `Polityka prywatności: ${name} (otwiera się w nowej karcie)`,
    servicesInCategory: 'Usługi w tej kategorii',
    controllerDetails: 'Administrator danych',
    privacyEyebrow: 'Prywatność',
    optionalCategories: 'Kategorie opcjonalne',
    aboutCategories: 'O tych kategoriach',
    providers: 'Dostawcy',
    searchPlaceholder: 'Szukaj',
    searchLabel: 'Szukaj dostawców lub celów',
    details: 'Szczegóły',
    previousPage: 'Poprzednia strona',
    nextPage: 'Następna strona',
    providerPages: 'Strony dostawców',
    providerRange: (from, to, total) => `${from}–${to} z ${total}`,
    providerCount: (count) =>
      `${count} ${{ one: 'dostawca', few: 'dostawcy', many: 'dostawców' }[plural('pl', count) as 'one' | 'few' | 'many'] ?? 'dostawcy'}`,
    noMatches: 'Brak pasujących dostawców.',
    clearSearch: 'Wyczyść wyszukiwanie',
    noProviders: 'Dla tej witryny nie wymieniono zewnętrznych dostawców.',
  },
  ro: {
    acceptAll: 'Acceptă toate',
    rejectAll: 'Respinge toate',
    manageChoices: 'Gestionează opțiunile',
    saveChoices: 'Salvează opțiunile',
    close: 'Închide',
    alwaysOn: 'Mereu active',
    preferencesIntro: 'Alegeți ce servicii opționale pot rula pe acest site. Serviciile necesare sunt mereu active.',
    privacyPolicy: 'Politica de confidențialitate',
    privacyPolicyNewTab: 'Politica de confidențialitate (se deschide într-o filă nouă)',
    providerPrivacyPolicy: (name) => `Politica de confidențialitate a ${name} (se deschide într-o filă nouă)`,
    servicesInCategory: 'Servicii din această categorie',
    controllerDetails: 'Operatorul de date',
    privacyEyebrow: 'Confidențialitate',
    optionalCategories: 'Categorii opționale',
    aboutCategories: 'Despre aceste categorii',
    providers: 'Furnizori',
    searchPlaceholder: 'Caută',
    searchLabel: 'Caută furnizori sau scopuri',
    details: 'Detalii',
    previousPage: 'Pagina anterioară',
    nextPage: 'Pagina următoare',
    providerPages: 'Pagini furnizori',
    providerRange: (from, to, total) => `${from}–${to} din ${total}`,
    providerCount: (count) =>
      `${count} ${{ one: 'furnizor', few: 'furnizori' }[plural('ro', count) as 'one' | 'few'] ?? 'de furnizori'}`,
    noMatches: 'Niciun furnizor potrivit.',
    clearSearch: 'Șterge căutarea',
    noProviders: 'Pentru acest site nu sunt listați furnizori externi.',
  },
  it: {
    acceptAll: 'Accetta tutto',
    rejectAll: 'Rifiuta tutto',
    manageChoices: 'Gestisci le scelte',
    saveChoices: 'Salva le scelte',
    close: 'Chiudi',
    alwaysOn: 'Sempre attivi',
    preferencesIntro:
      'Scegli quali servizi facoltativi possono essere attivi su questo sito. I servizi necessari sono sempre attivi.',
    privacyPolicy: 'Informativa sulla privacy',
    privacyPolicyNewTab: 'Informativa sulla privacy (si apre in una nuova scheda)',
    providerPrivacyPolicy: (name) => `Informativa sulla privacy di ${name} (si apre in una nuova scheda)`,
    servicesInCategory: 'Servizi in questa categoria',
    controllerDetails: 'Titolare del trattamento',
    privacyEyebrow: 'Privacy',
    optionalCategories: 'Categorie facoltative',
    aboutCategories: 'Informazioni sulle categorie',
    providers: 'Fornitori',
    searchPlaceholder: 'Cerca',
    searchLabel: 'Cerca fornitori o finalità',
    details: 'Dettagli',
    previousPage: 'Pagina precedente',
    nextPage: 'Pagina successiva',
    providerPages: 'Pagine dei fornitori',
    providerRange: (from, to, total) => `${from}–${to} di ${total} fornitori`,
    providerCount: (count) => (count === 1 ? '1 fornitore' : `${count} fornitori`),
    noMatches: 'Nessun fornitore corrispondente.',
    clearSearch: 'Cancella ricerca',
    noProviders: 'Per questo sito non sono elencati fornitori esterni.',
  },
  uk: {
    acceptAll: 'Прийняти всі',
    rejectAll: 'Відхилити всі',
    manageChoices: 'Керувати вибором',
    saveChoices: 'Зберегти вибір',
    close: 'Закрити',
    alwaysOn: 'Завжди увімкнено',
    preferencesIntro:
      'Оберіть, які необовʼязкові сервіси можуть працювати на цьому сайті. Необхідні сервіси завжди увімкнені.',
    privacyPolicy: 'Політика конфіденційності',
    privacyPolicyNewTab: 'Політика конфіденційності (відкриється в новій вкладці)',
    providerPrivacyPolicy: (name) => `Політика конфіденційності: ${name} (відкриється в новій вкладці)`,
    servicesInCategory: 'Сервіси в цій категорії',
    controllerDetails: 'Контролер даних',
    privacyEyebrow: 'Конфіденційність',
    optionalCategories: 'Необовʼязкові категорії',
    aboutCategories: 'Про ці категорії',
    providers: 'Постачальники',
    searchPlaceholder: 'Пошук',
    searchLabel: 'Пошук постачальників або цілей',
    details: 'Детальніше',
    previousPage: 'Попередня сторінка',
    nextPage: 'Наступна сторінка',
    providerPages: 'Сторінки постачальників',
    providerRange: (from, to, total) => `${from}–${to} з ${total}`,
    providerCount: (count) =>
      `${count} ${{ one: 'постачальник', few: 'постачальники', many: 'постачальників' }[plural('uk', count) as 'one' | 'few' | 'many'] ?? 'постачальника'}`,
    noMatches: 'Немає відповідних постачальників.',
    clearSearch: 'Скинути пошук',
    noProviders: 'Для цього сайту не вказано зовнішніх постачальників.',
  },
};

/** `de-CH`, `de_CH` and ` DE ` all read as `de`; anything outside the supported set is null. */
export function normalizeConsentUiLocale(value: string | null | undefined): ConsentUiLocale | null {
  const language = typeof value === 'string' ? value.trim().toLowerCase().split(/[-_]/)[0] : '';
  return CONSENT_UI_LOCALES.find((locale) => locale === language) ?? null;
}

/** The first supported candidate, in order; English when none is supported. */
export function resolveConsentUiLocale(candidates: ReadonlyArray<string | null | undefined>): ConsentUiLocale {
  for (const candidate of candidates) {
    const locale = normalizeConsentUiLocale(candidate);
    if (locale) return locale;
  }
  return 'en';
}

export function consentUiText(locale: ConsentUiLocale): ConsentUiText {
  return TEXT[locale] ?? TEXT.en;
}

export function isRtlConsentLocale(locale: ConsentUiLocale): boolean {
  return RTL_LOCALES.includes(locale);
}
